import React, { useEffect, useState } from "react";
import {
  TextInput,
  PasswordInput,
  Checkbox,
  Button,
  Anchor,
  Group,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useServerApi } from "../hooks/userServerApi";
import { showNotification } from "@mantine/notifications";
import { IconExclamationMark, IconLock, IconMail } from "@tabler/icons";
import { useSignIn, useIsAuthenticated, useSignOut } from "react-auth-kit";
import { useNavigate, Link } from "react-router-dom";
import { useLocation } from "react-router";
import AuthSplitLayout, { authFieldStyles } from "../components/authSplitLayout";

const Login = () => {
  const [api] = useServerApi();
  const signIn = useSignIn();
  const navigate = useNavigate();
  const isAuthenicated = useIsAuthenticated();
  const location = useLocation();
  const signOut = useSignOut();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenicated()) return;

    const query = new URLSearchParams(location.search);
    if (query.get("error") === "invalid_token") {
      signOut();
    }
  }, [isAuthenicated, location.search, signOut]);

  const form = useForm({
    initialValues: {
      email: "",
      keepSign: true,
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const handleError = ({ message }) => {
    let msg = "";
    switch (message) {
      case "ERR_NO_USER":
        msg = "No this user";
        break;
      case "ERR_USER_INACTIVE":
        msg = "The user is inactive";
        break;
      case "ERR_INVALID_PASSWORD":
        msg = "Invalid password";
        break;
      default:
        msg = message;
        break;
    }

    return showNotification({
      title: "Login Fail",
      color: "red",
      icon: <IconExclamationMark size={18} />,
      message: msg,
    });
  };

  const handleSuccess = async (result) => {
    // console.log(result);
    if (!result.data) return;
    const { a, t, u, w, expiresInHr } = result.data;
    // console.log("handleSuccess Logined:", u);
    const signed = await signIn({
      token: t,
      expiresIn: expiresInHr * 60,
      tokenType: "Bearer",
      authState: {
        ...u,
        a,
      },
    });
    // console.log("Signed", signed);
    if (signed) {
      setTimeout(() => {
        navigate("/home", { replace: true });
      }, 200);
    }
  };

  const handleSignIn = async ({ email, password }) => {
    setSubmitting(true);
    try {
      const result = await api.User.signIn(email, password);
      if (!result.success) return handleError(result);

      return handleSuccess(result);
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthSplitLayout>
      <form onSubmit={form.onSubmit((values) => handleSignIn(values))}>
        <TextInput
          label="Email"
          placeholder="your@email.com"
          required
          icon={<IconMail size={18} />}
          styles={authFieldStyles}
          {...form.getInputProps("email")}
        />
        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          required
          mt="md"
          icon={<IconLock size={18} />}
          styles={authFieldStyles}
          {...form.getInputProps("password")}
        />
        <Group position="apart" mt="lg">
          <Checkbox
            label="Remember me"
            sx={{ lineHeight: 1 }}
            {...form.getInputProps("keepSign", {
              type: "checkbox",
            })}
          />
          <Anchor
            component={Link}
            to="/forgetPassword"
            size="sm"
            sx={{ color: "#0c756e", fontWeight: 500 }}
          >
            Forgot password?
          </Anchor>
        </Group>
        <Button
          fullWidth
          mt="xl"
          type="submit"
          loading={submitting}
          radius={10}
          sx={{
            height: 44,
            backgroundColor: "#0e8a84",
            "&:hover": { backgroundColor: "#0c756e" },
          }}
        >
          Sign in
        </Button>
      </form>
    </AuthSplitLayout>
  );
};

export default Login;
